import { useRef, useMemo, useCallback } from "react";
import { Plus } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { DeletableBadge, Button } from "../..";
import { Popover, PopoverTrigger } from "../popover";
import { StringSelectionPopover } from "../StringSelectionPopover";
import type { StringSelectionPopoverOption } from "../StringSelectionPopover";

export interface TagColor {
  name: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  bgColor: string;
  textColor: string;
}

export interface TagListProps {
  items: string[];
  variant?:
    | "default"
    | "secondary"
    | "destructive"
    | "outline"
    | "chart-primary"
    | "chart-secondary"
    | "chart-tertiary"
    | "chart-accent";
  onRemoveTag?: (tag: string) => void;
  onAddTag?: (tag: string) => void;
  availableOptions?: string[];
  columnKey?: string;
  allowColorSelection?: boolean;
  globalTagColors?: Record<string, TagColor>;
  onSetGlobalTagColor?: (tag: string, color: TagColor) => void;
  searchPlaceholder?: string;
}

export const TagList = ({
  items,
  onRemoveTag,
  onAddTag,
  availableOptions = [],
  globalTagColors = {},
  searchPlaceholder,
}: TagListProps) => {
  const { t } = useTranslation("tags");
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToEnd = useCallback(() => {
    setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollLeft = containerRef.current.scrollWidth;
      }
    }, 0);
  }, []);

  const handleToggle = useCallback(
    (tag: string) => {
      if (items.includes(tag)) {
        onRemoveTag?.(tag);
      } else {
        onAddTag?.(tag);
        scrollToEnd();
      }
    },
    [items, onRemoveTag, onAddTag, scrollToEnd]
  );

  const handleCreate = useCallback(
    (value: string) => {
      onAddTag?.(value);
      scrollToEnd();
    },
    [onAddTag, scrollToEnd]
  );

  const allTags = useMemo(() => Array.from(new Set([...availableOptions, ...items])), [availableOptions, items]);

  const options: StringSelectionPopoverOption[] = useMemo(
    () => allTags.map((tag) => ({ label: tag, value: tag })),
    [allTags]
  );

  return (
    <div className="flex gap-1 items-center">
      {onAddTag && (
        <Popover>
          <PopoverTrigger
            asChild
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 flex-shrink-0 border-none bg-transparent hover:bg-muted/50 flex items-center justify-center shadow-none focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              aria-label={t("Add tag")}
            >
              <Plus className="h-2.5 w-2.5" />
            </Button>
          </PopoverTrigger>
          <StringSelectionPopover
            options={options}
            selectedValues={items}
            onToggle={handleToggle}
            searchPlaceholder={searchPlaceholder ?? t("Search or add tags...")}
            emptyText={t("No tags available")}
            className="w-48"
            align="start"
            creator={{
              render: (inputValue) => (
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    <Trans t={t}>
                      Add "<span className="font-semibold">{inputValue}</span>"
                    </Trans>
                  </span>
                </div>
              ),
              isValidNewOption: (inputValue) => {
                const trimmed = inputValue.trim();
                return trimmed.length > 0 && !allTags.some((tag) => tag.toLowerCase() === trimmed.toLowerCase());
              },
              onCreate: handleCreate,
            }}
          />
        </Popover>
      )}

      <div
        ref={containerRef}
        className="flex gap-1 overflow-x-auto max-w-[250px] w-fit items-center [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {items.map((item, index) => {
          const itemColor = globalTagColors[item];
          const badgeVariant = itemColor ? itemColor.variant : "secondary";

          let styleProps = {};
          let customColorClass = "bg-gray-200 text-gray-700 border-transparent";

          if (itemColor) {
            if (itemColor.bgColor.startsWith("#")) {
              const textColor = itemColor.textColor.includes("white") ? "white" : "black";
              styleProps = {
                backgroundColor: itemColor.bgColor,
                color: textColor,
                borderColor: "transparent",
              };
              customColorClass = "border-transparent";
            } else {
              customColorClass = `${itemColor.bgColor} ${itemColor.textColor} border-transparent`;
            }
          }

          return (
            <DeletableBadge
              key={index}
              variant={badgeVariant}
              className={`whitespace-nowrap flex-shrink-0 h-5 ${customColorClass}`}
              style={styleProps}
              onDelete={onRemoveTag ? () => onRemoveTag(item) : undefined}
            >
              {item}
            </DeletableBadge>
          );
        })}
      </div>
    </div>
  );
};
